export type FeatureFlagStatus = 'enabled' | 'disabled' | 'locked' | 'scheduled';

export type FeatureFlagEnvironment =
  'development' | 'sandbox' | 'stage' | 'production';

export type RolloutStrategyType =
  | 'boolean'
  | 'percentage'
  | 'userTargeting'
  | 'groupTargeting'
  | 'roleTargeting'
  | 'productTargeting'
  | 'environmentTargeting'
  | 'dateBasedActivation'
  | 'killSwitch'
  | 'abExperiment';

export interface FeatureTargetRule {
  id: string;
  strategy: RolloutStrategyType;
  include?: string[];
  exclude?: string[];
  percentage?: number;
  startAt?: string;
  endAt?: string;
}

export interface FeatureEvaluationContext {
  userId?: string;
  roleIds?: string[];
  groupIds?: string[];
  productId?: string;
  environment?: FeatureFlagEnvironment;
  now?: Date;
}

export interface FeatureStrategyEvaluatorInput {
  enabled: boolean;
  rolloutPercentage: number;
  targetRules: FeatureTargetRule[];
  context: FeatureEvaluationContext;
}

export type FeatureStrategyEvaluator = (
  input: FeatureStrategyEvaluatorInput,
) => boolean;

function hashToPercent(input: string): number {
  let hash = 0;

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash) % 100;
}

const booleanEvaluator: FeatureStrategyEvaluator = (input) => input.enabled;

const percentageEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const userKey = input.context.userId ?? 'anonymous';
  return hashToPercent(userKey) < input.rolloutPercentage;
};

const userTargetingEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const userId = input.context.userId;

  if (!userId) {
    return false;
  }

  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'userTargeting',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    if (rule.exclude?.includes(userId)) {
      return false;
    }

    return rule.include?.includes(userId) ?? false;
  });
};

const groupTargetingEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const groupIds = input.context.groupIds ?? [];
  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'groupTargeting',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    const excluded = (rule.exclude ?? []).some((groupId) =>
      groupIds.includes(groupId),
    );

    if (excluded) {
      return false;
    }

    return (rule.include ?? []).some((groupId) => groupIds.includes(groupId));
  });
};

const roleTargetingEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const roleIds = input.context.roleIds ?? [];
  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'roleTargeting',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    const excluded = (rule.exclude ?? []).some((roleId) =>
      roleIds.includes(roleId),
    );

    if (excluded) {
      return false;
    }

    return (rule.include ?? []).some((roleId) => roleIds.includes(roleId));
  });
};

const productTargetingEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const productId = input.context.productId;

  if (!productId) {
    return false;
  }

  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'productTargeting',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    if (rule.exclude?.includes(productId)) {
      return false;
    }

    return rule.include?.includes(productId) ?? false;
  });
};

const environmentTargetingEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const environment = input.context.environment;

  if (!environment) {
    return false;
  }

  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'environmentTargeting',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    if (rule.exclude?.includes(environment)) {
      return false;
    }

    return rule.include?.includes(environment) ?? false;
  });
};

const dateBasedActivationEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const now = input.context.now ?? new Date();
  const matchingRules = input.targetRules.filter(
    (rule) => rule.strategy === 'dateBasedActivation',
  );

  if (!matchingRules.length) {
    return true;
  }

  return matchingRules.some((rule) => {
    const startAt = rule.startAt ? new Date(rule.startAt) : null;
    const endAt = rule.endAt ? new Date(rule.endAt) : null;

    if (startAt && now < startAt) {
      return false;
    }

    if (endAt && now > endAt) {
      return false;
    }

    return true;
  });
};

const killSwitchEvaluator: FeatureStrategyEvaluator = (input) => {
  const hasKillSwitchOff = input.targetRules.some(
    (rule) =>
      rule.strategy === 'killSwitch' &&
      (rule.include?.includes('off') ?? false),
  );

  if (hasKillSwitchOff) {
    return false;
  }

  return input.enabled;
};

const abExperimentEvaluator: FeatureStrategyEvaluator = (input) => {
  if (!input.enabled) {
    return false;
  }

  const userKey = input.context.userId ?? 'anonymous';
  return hashToPercent(userKey) < input.rolloutPercentage;
};

export const featureStrategyEvaluators: Record<
  RolloutStrategyType,
  FeatureStrategyEvaluator
> = {
  boolean: booleanEvaluator,
  percentage: percentageEvaluator,
  userTargeting: userTargetingEvaluator,
  groupTargeting: groupTargetingEvaluator,
  roleTargeting: roleTargetingEvaluator,
  productTargeting: productTargetingEvaluator,
  environmentTargeting: environmentTargetingEvaluator,
  dateBasedActivation: dateBasedActivationEvaluator,
  killSwitch: killSwitchEvaluator,
  abExperiment: abExperimentEvaluator,
};
